/**
 * @fileoverview Confirmar o e-mail pelo link (30/10/2026, etapa 4 dos planos):
 * o código vem depois do # (lido uma vez e tirado do endereço) e é usado ao
 * abrir. Confirmado, segue sozinha (03/11/2026): no navegador do cadastro, entra
 * na conta; com uma sessão aberta aqui, volta ao sistema nela (logada como outra
 * pessoa, antes avisa de quem era o e-mail); nos outros casos, a tela de entrada
 * com o e-mail preenchido. Link que não vale mais: pede um novo, com a mesma
 * resposta exista ou não a conta.
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AuthLayout from "../../components/AuthLayout";
import useLanguage from "../../hooks/useLanguage";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import { EMAIL_RE, announceEmailConfirmed, errorCode, forgetSignupBrowser, readSignupBrowser, takeLinkCode } from "../../components/AuthLayout/authHelpers";

const VerifyEmail = () => {
  useLanguage(); // trocou o idioma no globo: redesenha já traduzida
  const classes = useAuthFormStyles();
  const history = useHistory();
  const { isAuth, user, loading, startSession } = useContext(AuthContext);
  const [code] = useState(takeLinkCode);
  const [state, setState] = useState(code ? "checking" : "invalid");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const [confirmedOther, setConfirmedOther] = useState("");
  const tried = useRef(false);

  // O link inválido (ERR_AUTH_LINK_INVALID) pede um novo; falha de rede ou do
  // servidor deixa tentar de novo com o mesmo código (ele já saiu do endereço).
  const confirm = () => {
    setState("checking");
    // A sessão aberta neste navegador segue: o link não abre outra por cima dela.
    const browserProof = isAuth ? null : readSignupBrowser();
    openApi
      .post("/auth/verify-email", { code, ...(browserProof ? { browserProof } : {}) })
      .then(({ data }) => {
        if (data?.token) {
          forgetSignupBrowser();
          startSession(data, { successText: i18n.t("auth.verify.okTitle"), replace: true });
        } else if (isAuth && data?.email && user?.email && data.email.toLowerCase() !== user.email.toLowerCase()) {
          setConfirmedOther(data.email);
          setState("other");
        } else if (isAuth || localStorage.getItem("token")) {
          // Com a sessão aberta aqui (ou recém-aberta pelo mesmo link em outra aba). A
          // tela inteira recarrega: a faixa "confirme seu e-mail" some com o cadastro relido.
          window.location.replace("/");
        } else {
          history.replace("/login", { emailConfirmed: true, email: data?.email || "" });
        }
        // Por último: a aba do cadastro, ao saber, já encontra a sessão aberta.
        announceEmailConfirmed();
      })
      .catch((err) => setState(errorCode(err) === "ERR_AUTH_LINK_INVALID" ? "invalid" : "failed"));
  };

  // Espera a conferência da sessão guardada: é ela que diz se há uma aberta.
  useEffect(() => {
    if (!code || tried.current || loading) return;
    tried.current = true;
    confirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, loading]);

  useEffect(() => {
    if (isAuth && user?.email) setEmail((current) => current || user.email);
  }, [isAuth, user]);

  const resend = async (e) => {
    e.preventDefault();
    if (sending || !EMAIL_RE.test(email.trim())) return;
    setSending(true);
    try {
      await openApi.post("/auth/verify-email/resend", { email: email.trim() });
      setResent(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const goOn = () => (isAuth ? window.location.assign("/") : history.push("/login"));

  return (
    <AuthLayout>
      {state === "checking" && (
        <div className={classes.view} role="status" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CircularProgress size={20} />
          <span>{i18n.t("auth.verify.checking")}</span>
        </div>
      )}
      {state === "other" && (
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.verify.okTitle")}</h1>
          <div className={`${classes.notice} ${classes.noticeOk}`} role="status">{i18n.t("auth.verify.otherAccount", { email: confirmedOther, current: user?.email })}</div>
          <button type="button" className={classes.button} onClick={goOn}>{i18n.t("auth.verify.goToApp")}</button>
        </div>
      )}
      {state === "failed" && (
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.verify.failedTitle")}</h1>
          <p className={classes.subtitle}>{i18n.t("auth.errors.generic")}</p>
          <button type="button" className={classes.button} onClick={confirm}>{i18n.t("auth.verify.retry")}</button>
        </div>
      )}
      {state === "invalid" && (
        <form className={classes.view} noValidate onSubmit={resend}>
          <h1 className={classes.title}>{i18n.t("auth.invalidLinkTitle")}</h1>
          <p className={classes.subtitle}>{i18n.t("auth.verify.invalid")}</p>
          {resent ? (
            <div className={classes.notice} role="status">{i18n.t("auth.verify.resent")}</div>
          ) : (
            <>
              <div className={classes.field}>
                <label className={classes.label} htmlFor="verify-email">{i18n.t("auth.fields.email")}</label>
                <input id="verify-email" type="email" className={classes.input} value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button type="submit" className={classes.button} disabled={sending || !EMAIL_RE.test(email.trim())}>
                {sending && <CircularProgress size={18} className={classes.spinner} />}
                {i18n.t("auth.verify.resend")}
              </button>
            </>
          )}
          <button type="button" className={classes.secondary} onClick={goOn}>
            {isAuth ? i18n.t("auth.verify.goToApp") : i18n.t("auth.backToLogin")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default VerifyEmail;
